import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, KeyIcon, PlusCircleIcon, SearchIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr, ActionsColumn, IAction } from '@patternfly/react-table';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import PubkeyDeleteModal from './PubkeyDeleteModal';
import { useApiFetch } from '../../../api';
import { usePubkeys } from '../../../hooks/usePubkeys';
import { useUserContext } from '../../../contexts/user/UserContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { PubkeySummary } from '../../../types/models/Pubkey';

const PubkeyList: React.FunctionComponent = () => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const currentUser = useUserContext();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { pubkeys, loading, error, refetch } = usePubkeys();

  const [deleteTarget, setDeleteTarget] = useState<PubkeySummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const filteredPubkeys = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return pubkeys;
    return pubkeys.filter((p) => p.label.toLowerCase().includes(q));
  }, [pubkeys, debouncedSearch]);

  const paginatedPubkeys = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredPubkeys.slice(start, start + perPage);
  }, [filteredPubkeys, page, perPage]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      const response = await apiFetch(`/datacenters/pubkeys/${deleteTarget.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      addNotification({
        title: 'Public key deleted',
        srTitle: 'Success alert',
        description: `Public key "${deleteTarget.label}" has been deleted.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      await refetch();
    } catch (err) {
      addNotification({
        title: 'Error deleting public key',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const getRowActions = (pubkey: PubkeySummary): IAction[] => {
    if (currentUser === null || pubkey.userID !== currentUser.id) return [];
    return [
      {
        title: 'Edit',
        onClick: () => navigate(`/datacenter/pubkeys/${pubkey.id}/edit`),
      },
      {
        isSeparator: true,
      },
      {
        title: 'Delete',
        onClick: () => setDeleteTarget(pubkey),
      },
    ];
  };

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load public keys"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (pubkeys.length === 0) {
      return (
        <EmptyState titleText="No public keys" icon={KeyIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>No SSH public keys have been added to your organization yet.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/pubkeys/create" />}>
                Create public key
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by label..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredPubkeys.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredPubkeys.length === 0 ? (
          <EmptyState titleText="No public keys match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all public keys.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setSearchValue('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Public keys list" variant="compact">
            <Thead>
              <Tr>
                <Th>Label</Th>
                <Th>Owner</Th>
                <Th screenReaderText="Actions" />
              </Tr>
            </Thead>
            <Tbody>
              {paginatedPubkeys.map((pubkey) => {
                const isOwner = currentUser !== null && pubkey.userID === currentUser.id;
                const actions = getRowActions(pubkey);
                return (
                  <Tr key={pubkey.id}>
                    <Td dataLabel="Label">
                      <Link to={`/datacenter/pubkeys/${pubkey.id}`}>{pubkey.label}</Link>
                    </Td>
                    <Td dataLabel="Owner">{isOwner && <Label color="blue">You</Label>}</Td>
                    <Td isActionCell>{actions.length > 0 && <ActionsColumn items={actions} />}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        {filteredPubkeys.length > 0 && (
          <Pagination
            itemCount={filteredPubkeys.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}

        {deleteTarget && (
          <PubkeyDeleteModal
            isOpen={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDelete}
            pubkeyLabel={deleteTarget.label}
            isDeleting={isDeleting}
          />
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem isActive>Public Keys</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <KeyIcon />
              </Icon>
              Public Keys
            </Content>
            <Content component="p">Manage SSH public keys for your organization</Content>
          </FlexItem>
          {!loading && (
            <FlexItem>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                component={(props) => <Link {...props} to="/datacenter/pubkeys/create" />}
              >
                Create public key
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
    </>
  );
};

export default PubkeyList;
